import type { FilterNode } from '@ofmos/shared/filter-ast';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import {
  pagedIds,
  type RecordListOptions,
  resetRecordListPages,
  useInfiniteRecordList,
  useRecordCount,
} from '../../../data/hooks';
import { Button, EmptyState, useIsDesktop } from '../../../ui';
import { EMPTY_RELATION_CONTEXT, type RelationContext } from '../../filters/filterModel';
import { relationRefusalOf, relationRefusalSentence } from '../../filters/relationRefusal';
import { FilterRefusedBanner } from '../views/FilterRefusedBanner';
import type { ViewState } from '../views/viewState';
import { CheckpointFilterBar, useCheckpointFilter } from './CheckpointFilter';
import { GroupedRecordCardList } from './GroupedRecordCardList';
import { GroupedRecordTable, GroupFetchingIndicator } from './GroupedRecordTable';
import { useGroupDisplay } from './groupDisplay';
import { RecordCardList } from './RecordCardList';
import { RecordListFooter } from './RecordListFooter';
import { RecordTable } from './RecordTable';
import { useRowSelection } from './RowSelectionProvider';
import type { RecordSegment } from './TableToolbar';
import { sortToOrderBy } from './tableModel';
import { useGroupedTable } from './useGroupedTable';
import { useTableControls } from './useTableControls';

/**
 * The record list, in the two shapes its two viewports need.
 *
 * Below the desktop breakpoint this is NOT a table: it is a card list, because a
 * dense grid on a phone is the one thing this product must not repeat.
 *
 * CONTROLLED SINCE THIS MODULE (D20). The sort, the filter and the visible columns used
 * to be `useState` in here, which is why they died on every unmount - switching to the
 * board discarded all three, and a saved view could not describe a state that lived in a
 * leaf. They come in as `state` and go out through `onStateChange`.
 *
 * THE ARCHIVED SEGMENT IS STILL NOT PART OF A VIEW (D15) - a view is a thing you send
 * somebody, and "the archive of this object" is a place you go rather than a thing you
 * send, so it is neither stored nor in the URL. It no longer LIVES here, though: the
 * phone reaches it through the page's Adjust sheet, which exists in board mode too, and
 * a control cannot be rendered by a component that is unmounted at the time. It comes in
 * as a prop. The visible consequence is that leaving for the board and coming back keeps
 * the archive open instead of silently resetting to Active - which is what D15 already
 * promised for view switches.
 *
 * THE TOOLBAR IS DESKTOP-ONLY SINCE 2026-07-29. Below the breakpoint its four controls
 * are sections of the Adjust sheet, and rendering both would put every one of them on
 * the screen twice - once invisibly, still in the accessibility tree.
 */
export interface RecordTableViewProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  /**
   * D27's anchor set plus the fields behind it (D21), read ONCE by the page and handed
   * down. Two placements of one editor must not read it twice or disagree about it, and
   * the page needs it anyway - `reconcileView` validates a stored anchor against it.
   */
  readonly relations?: RelationContext | undefined;

  readonly state: ViewState;
  /**
   * The filter as the SERVER sees it: D17's relative-date tokens already resolved
   * against the reader's clock. The stored form travels inside `state.filter`, and the
   * toolbar edits that one - so a token survives an edit instead of being frozen into
   * the day it was first rendered.
   */
  readonly filter: FilterNode | undefined;
  readonly onStateChange: (next: (current: ViewState) => ViewState) => void;
  /**
   * D15's Active | Archived, owned by the page. The CONTROL that changes it left this
   * component with the toolbar row (P4-4): on the desktop it is the "Show Archived" token in
   * the header row, on the phone it is the Adjust sheet's section. The table only has to
   * know WHICH list it is showing.
   */
  readonly segment: RecordSegment;
  /** D23: the empty state's own CTA, which is the page's New button by another name. */
  readonly onCreate: () => void;
  /**
   * ROWS PER PAGE, LIFTED TO THE PAGE (P4-5). It was a `useLocalChoice` in here, which was
   * right while the control that changes it sat in this component's own footer. The control
   * moved into the view options (owner, 2026-09-10: "Load more looks unfinished" - the
   * footer is a caption row now), and the view options live in the page's header row, so the
   * value has to live above both. The storage key, the choices and the server default are
   * unchanged; only the component holding the `useState` moved, which is the same lift D20
   * made for the filter and the sort.
   */
  readonly pageSize: number;
  /** Still needed here for the PHONE footer, which keeps its rows-per-page control (P4-5). */
  readonly onPageSizeChange: (next: number) => void;
}

export function RecordTableView({
  object,
  relations = EMPTY_RELATION_CONTEXT,
  fields,
  state,
  filter,
  onStateChange,
  segment,
  onCreate,
  pageSize,
  onPageSizeChange,
}: RecordTableViewProps) {
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const archived = segment === 'archived';

  // The same derivations the page's Adjust sheet reads, from the same pure hook.
  const { columns, shownColumns, sort, setFilter } = useTableControls({
    object,
    fields,
    state,
    onStateChange,
  });

  // Every field is selected, not just the visible ones: hiding a column would
  // otherwise change the list's cache key and refetch the page (INV-13), and the
  // record page a row links to reads the same canonical entries.
  const select = useMemo(() => columns.map((column) => column.field.id), [columns]);

  /**
   * WHAT "STAGE" MEANS ON THIS VIEW (P4 round 2, finding F2).
   *
   * The row menu's stage submenu used to be resolved over EVERY field of the object, on the
   * reasoning that a menu losing an item because somebody hid a column would depend on the
   * wrong thing. On the review fixture that produced "Set Language": Language is the object's
   * first SELECT by position, the view shows Status, and the menu offered to change a field
   * the reader could not see. The view is the right scope - it is what the reader is looking
   * at - so the board field is used when the view keeps it and the view's first select column
   * otherwise, and a view with no select column has no stage and no item (S9 hides what does
   * not apply rather than dimming it).
   */
  const viewFields = useMemo(() => shownColumns.map((column) => column.field), [shownColumns]);
  const orderBy = sortToOrderBy(sort);

  /**
   * THREE READS, AND THE FLAT PAIR IS ENABLED ONLY WHILE THE LIST IS FLAT
   * (group-by-aggregation.md D22, `table-completion.md` D1, D5).
   *
   * The FLAT path is a CURSOR WALK since this module: `useInfiniteRecordList` at the page size
   * above, plus a count-only SIBLING over the same filter. It used to be one page of fifty rows
   * with no cursor and no count, which is the silence this module removes.
   *
   * The GROUPED path is a different hook against a different route, and it returns null when
   * the view stores no dimension. All of them are mounted unconditionally, because a hook
   * cannot be called conditionally and the alternative - two components with two lifetimes -
   * would remount the whole table every time somebody grouped or ungrouped it.
   */
  const grouped = useGroupedTable({
    object,
    fields,
    columns,
    shownColumns,
    state,
    filter,
    orderBy,
    archived,
  });

  const listOptions: RecordListOptions = {
    select,
    filter,
    orderBy,
    limit: pageSize,
    archivedOnly: archived,
    enabled: grouped === null,
  };

  const list = useInfiniteRecordList(object.id, listOptions);

  /**
   * THE TOTAL IS ITS OWN READ (D5), and the sibling's options are the LIST'S OPTIONS with one
   * member replaced. Deriving them rather than enumerating them is the mechanism: written out
   * by hand, `archivedOnly` is the member that goes missing later, and the Archived segment
   * would then show the LIVE total under archived rows - a wrong NUMBER on a screen, which by
   * this repository's own vocabulary is the refusal case rather than the truncation case.
   *
   * The `select` is ONE field id - the object's label identifier - and that is what keeps this
   * entry's key stable while the reader hides and shows columns. `useRecordCount` owns the
   * `limit` and the `withTotalCount`, so neither number is written at a call site.
   */
  const count = useRecordCount(object.id, {
    ...listOptions,
    select: [object.labelIdentifierFieldId],
  });

  /**
   * Which groups are open, and which one has asked for all its rows. Transient by design
   * (D17) and keyed by the dimension as well as the object: regrouping by another field
   * produces a different set of groups, and carrying a collapse map across would open and
   * close them at random.
   */
  const display = useGroupDisplay(`${object.id}:${state.groupBy?.[0]?.fieldId ?? ''}`);

  /**
   * THE ROWS ON SCREEN, flattened in the one place a record list is flattened (D12): every
   * loaded page in order, with duplicate ids removed, and `undefined` - never `[]` - while the
   * first page is in flight, because that is what makes `RecordTable` paint its skeleton.
   */
  const rowIds = pagedIds(list.data);

  /**
   * EMPTINESS IS THE GRAND TOTAL, not the length of the group list. A SELECT dimension
   * synthesises its whole option list (D29), so a grouped table over an empty object would
   * otherwise render a column of zeroes where the empty state teaches.
   *
   * ON THE FLAT PATH IT IS THE PAGED SHAPE NOW (`table-completion.md` D14), and this is the
   * line that would have broken silently: `useInfiniteRecordList` stores `{pages, pageParams}`,
   * so the old `list.data?.ids.length === 0` becomes `undefined`, which is falsy - the empty
   * state would never render and an empty object would paint an empty table with a status row
   * reading "Showing all 0 customers.". THE COUNT DOES NOT DECIDE EMPTINESS either, though it
   * could: it is a second statement that can arrive later or fail on its own, and emptiness is
   * a property of the rows the screen holds.
   */
  const isEmpty = grouped === null ? rowIds?.length === 0 : grouped.total?.count === 0;

  /**
   * D6's "Late" chip, over the rows this walk has already loaded. Flat mode only - the reason
   * is in `CheckpointFilter.tsx` - and absent entirely on a view whose columns carry no
   * deadline, which is every view in the product but the two the seed opens on onboarding.
   */
  const checkpoints = useCheckpointFilter({
    objectId: object.id,
    columns: shownColumns,
    rowIds,
    enabled: grouped === null,
  });
  const shownRowIds = checkpoints.visibleIds;

  /**
   * THE ORDER A SELECTION EXTENDS OVER IS THE ORDER THIS COMPONENT IS SHOWING (P4-2), which
   * is why it is published from here rather than derived by the provider: "from here to
   * there" and "the next row down" are questions about the rows on screen after the sort,
   * the filter, the paging walk and D6's Late chip have all had their say, and this is the
   * only place that knows all four. A grouped table publishes nothing, so a range gesture
   * there selects one row rather than a range across groups it cannot order.
   */
  const selection = useRowSelection();
  const publishOrder = selection.setOrder;
  useEffect(() => {
    publishOrder(shownRowIds ?? []);
  }, [publishOrder, shownRowIds]);

  /**
   * THE ERROR ARM BELONGS TO A TABLE WITH NO ROWS (D26). query-core sets `status: "error"`
   * unconditionally on a failed fetch, even when the entry holds data - there is no
   * background-error arm that preserves `success` - so without this a failed APPEND replaces the
   * 250 rows somebody was reading with a red paragraph, and nothing anywhere restarts the walk.
   * A FIRST page that fails still gets the paragraph, which is right: there is nothing else to
   * show. A failed append keeps its rows and becomes the status row's failure shape, which is
   * where a message about the NEXT rows belongs.
   */
  const loadError =
    grouped === null ? (list.isError && rowIds === undefined ? list.error : null) : grouped.error;
  const appendError = grouped === null && list.isError && rowIds !== undefined ? list.error : null;
  /**
   * D11 M4. A `forbidden` carrying `{objectId, anchorFieldId, site: 'filter'}` is the ONE
   * refusal this screen turns into copy instead of into the generic error paragraph, and
   * it is recognised by that shape rather than by matching a message.
   */
  const refusal = relationRefusalOf(loadError ?? appendError);

  /**
   * THE STATUS ROW, once, for both viewports (D8, D11). Absent until the first page has landed:
   * a sentence about a list nobody can see yet would be the loading state lying, and the two
   * list components render placeholders in that interval.
   */
  const footer =
    grouped !== null || rowIds === undefined ? undefined : (
      <RecordListFooter
        object={object}
        layout={isDesktop ? 'bar' : 'list-item'}
        displayedRows={rowIds.length}
        pageCount={list.data?.pages.length ?? 0}
        pageSize={pageSize}
        totalCount={count.data?.totalCount}
        hasNextPage={list.hasNextPage === true}
        isFetchingNextPage={list.isFetchingNextPage}
        archived={archived}
        error={appendError}
        onShowMore={() => void list.fetchNextPage()}
        onRetry={() => void list.fetchNextPage()}
        onRestart={() => resetRecordListPages(queryClient, object.id, listOptions)}
        onPageSizeChange={onPageSizeChange}
      />
    );

  return (
    /* `pt-3` replaces the toolbar's own vertical padding on the phone, where the
       toolbar is not rendered; the desktop keeps exactly the spacing it had.

       A FLEX COLUMN FROM THE DESKTOP BREAKPOINT UP (polish wave 2): the toolbar keeps its
       own height and the table card takes everything the page has left, which is what lets
       the card be the scrollport. Below `md:` this is the block it always was - the phone
       scrolls its page, and a card list inside a fixed-height box would be two scrollbars
       for one list. */
    <section className="min-w-0 pt-3 md:flex md:shrink-0 md:flex-col md:pt-0">
      {/* THE TOOLBAR ROW LEFT THIS COMPONENT (P4-4). Its four controls are capsules in the
          list's HEADER ROW now, beside the view chip and the filter tokens, which is Mail's
          construction and the one row the spec asks for. The row is rendered by the page,
          because the view chip and the filter tokens are the page's state; this component
          keeps the table and nothing above it. */}
      {refusal !== null ? (
        /**
         * D11's browser half: a filter the reader may not run is a STATE, not a dead page.
         * It replaces the table rather than sitting above it, because there is no list
         * underneath - and no second, unfiltered query is issued to make one.
         */
        <FilterRefusedBanner
          message={relationRefusalSentence(refusal, state.filter, relations)}
          onClear={() => setFilter(null)}
        />
      ) : loadError !== null ? (
        <p
          role="alert"
          className="rounded-surface border border-danger bg-danger-subtle px-4 py-3 text-sm text-danger"
        >
          {/*
            The GROUPED branch says "groups" rather than "records", and it matters in the
            review build: the fixture REFUSES a date bucket and a currency aggregate with a
            typed error whose message says "not available in the review build" (D28), and
            that sentence has to arrive under a heading that is about grouping rather than
            about the records themselves, which loaded fine.
          */}
          {`${grouped === null ? 'These records' : 'These groups'} could not be loaded. ${loadError.message}`}
        </p>
      ) : isEmpty ? (
        /**
         * D23's TRIAD, and it stays three.
         *
         * ARCHIVED keeps its copy and its deliberate ABSENCE of an action: an empty
         * archive is the HEALTHY state, there is nothing to do here, and inventing a
         * button would be noise. The first draft of D23, written against a two-way
         * branch, would have put a create CTA on exactly this screen.
         *
         * UNFILTERED teaches. Every object has carried a real sentence since `80d6d25`
         * and the UI read it in exactly ONE place - the page-header subtitle - so the
         * screen a first-time user actually meets said nothing about what this object is
         * for. It says it here, with the one action that starts.
         *
         * FILTERED offers the way BACK, never create: a record made while a view is
         * narrowed would very likely not match it and would appear to vanish.
         */
        <EmptyState
          icon={archived ? 'trash' : filter === undefined ? 'table' : 'filter'}
          title={
            archived
              ? `No archived ${object.labelPlural}`
              : filter === undefined
                ? `No ${object.labelPlural}`
                : `No ${object.labelPlural} match this view`
          }
          description={
            archived
              ? `Archived ${object.labelPlural.toLowerCase()} appear here and can be restored.`
              : filter === undefined
                ? (object.description ?? `New ${object.labelPlural.toLowerCase()} appear here.`)
                : 'Change the filter, or clear it to see everything again.'
          }
          action={
            archived || filter !== undefined ? undefined : (
              /* ITS NAME SAYS WHERE IT STANDS (BR-PARTS): the toolbar carries a button with the
                 same words, "New <object>", and two controls with one name are one control to a
                 screen reader and to a test. The label a person sees stays; the accessible name
                 starts with it (WCAG 2.5.3) and adds the empty state's own heading. */
              <Button
                variant="primary"
                icon="plus"
                onClick={onCreate}
                aria-label={`New ${object.labelSingular}, No ${object.labelPlural}`}
              >
                {`New ${object.labelSingular}`}
              </Button>
            )
          }
        />
      ) : grouped !== null ? (
        <>
          {/*
            HubSpot's in-flight indicator, adopted in spirit (D24): an aggregate being
            recomputed after a drag SAYS SO, rather than showing a stale number that looks
            correct. The mechanism is D25's - the grouped entry is invalidated by every
            write path in the app, and TanStack Query's `isFetching` is the indicator.
          */}
          <div className="flex min-h-5 justify-end pb-1">
            <GroupFetchingIndicator active={grouped.isFetching} />
          </div>
          {isDesktop ? (
            <GroupedRecordTable
              object={object}
              columns={shownColumns}
              fields={viewFields}
              boardGroupFieldId={state.boardGroupFieldId}
              grouped={grouped}
              display={display}
              viewFilter={filter}
              orderBy={orderBy}
              select={select}
              archived={archived}
            />
          ) : (
            <GroupedRecordCardList
              object={object}
              columns={shownColumns}
              fields={viewFields}
              boardGroupFieldId={state.boardGroupFieldId}
              grouped={grouped}
              display={display}
              viewFilter={filter}
              orderBy={orderBy}
              select={select}
              archived={archived}
            />
          )}
        </>
      ) : (
        <>
          <CheckpointFilterBar
            filter={checkpoints}
            loadedRows={rowIds?.length ?? 0}
            hasMore={list.hasNextPage === true}
          />
          {checkpoints.segment === 'late' && shownRowIds?.length === 0 ? (
            /**
             * A SECTION'S EMPTY STATE IS A LINE (`ios26-mechanisms.md`, empty states): the
             * block with an icon and an action is for a SCREEN with nothing on it, and this
             * screen has rows - the reader narrowed them to none and the way back is the chip
             * one line above. Saying "nothing is late" is also the good news.
             */
            <p className="py-6 text-sm text-ink-muted">
              {`Nothing is late among the ${rowIds?.length ?? 0} rows loaded.`}
            </p>
          ) : isDesktop ? (
            <RecordTable
              object={object}
              columns={shownColumns}
              fields={viewFields}
              boardGroupFieldId={state.boardGroupFieldId}
              recordIds={shownRowIds}
              archived={archived}
              footer={footer}
            />
          ) : (
            <RecordCardList
              object={object}
              columns={shownColumns}
              fields={viewFields}
              boardGroupFieldId={state.boardGroupFieldId}
              recordIds={shownRowIds}
              archived={archived}
              footer={footer}
            />
          )}
        </>
      )}
    </section>
  );
}
